import useDocumentTitle from '../hooks/useDocumentTitle';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { getApiErrorMessage } from '../lib/errorUtils';
import wsService from '../services/websocket';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import CommunicationsSmsTab from '../components/CommunicationsSmsTab';   // v3.21-1736
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  MessageSquare, 
  Bell, 
  Plus,
  Loader2, 
  Send,
  User,
  Building2,
  AlertCircle,
  AlertTriangle,
  Info,
  Trash2,
  Edit,
  Check,
  FileText,
  Clock,
  DollarSign,
  ExternalLink,
  RefreshCw,
  ArrowRight,
  Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatET } from '../utils/timeUtils';
// v3.18-1377 GMAIL-002 Phase 1 — Mailbox shell (Inbox/Sent/Drafts/Compose/Switcher)
import MailboxTab from '../components/MailboxTab';
// v3.19-1422 COMMS-MODERN — new tabs.
import InternalMessagingTab from '../components/InternalMessagingTab';
import NativeMessagingTab from '../components/NativeMessagingTab';
import BulletinBoardTab from '../components/BulletinBoardTab';
import CompanyDriveTab from '../components/CompanyDriveTab';

// Priority colors and icons for announcements
const priorityConfig = {
  info: { color: 'bg-blue-100 text-blue-800 border-blue-300', icon: Info, label: 'Info' },
  warning: { color: 'bg-orange-100 text-orange-800 border-orange-300', icon: AlertTriangle, label: 'Warning' },
  critical: { color: 'bg-red-100 text-red-800 border-red-300', icon: AlertCircle, label: 'Critical' },
};

// Conversation Card Component
const ConversationCard = ({ conversation, onClick, isSelected }) => (
  <div
    onClick={onClick}
    className={`p-4 border-b cursor-pointer transition-colors ${
      isSelected ? 'bg-accent/10 border-l-4 border-l-accent' : 'hover:bg-slate-50'
    }`}
    data-testid={`conversation-${conversation.user_id}`}
  >
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center text-white font-semibold">
        {conversation.user_name?.charAt(0) || 'U'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className="font-medium text-slate-800 truncate">{conversation.user_name}</p>
          {conversation.unread_count > 0 && (
            <Badge className="bg-accent text-white">{conversation.unread_count}</Badge>
          )}
        </div>
        <p className="text-sm text-slate-500 truncate">{conversation.user_company}</p>
        <p className="text-xs text-slate-400 truncate mt-1">{conversation.last_message}</p>
      </div>
    </div>
  </div>
);

// Message Bubble Component
const MessageBubble = ({ message, isOwn, canDelete, onDelete }) => (
  <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3 group`}>
    <div
      className={`max-w-[70%] rounded-lg px-4 py-2 relative ${
        isOwn ? 'bg-accent text-white' : 'bg-slate-100 text-slate-800'
      }`}
    >
      {!isOwn && (
        <p className="text-xs font-medium mb-1 opacity-70">{message.sender_name}</p>
      )}
      <p className="text-sm">{message.content}</p>
      <div className={`flex items-center justify-between mt-1 gap-2`}>
        <p className={`text-xs ${isOwn ? 'text-white/70' : 'text-slate-400'}`}>
          {formatET(message.created_at, 'MMM d, h:mm a')}
        </p>
        {canDelete && (
          <button
            onClick={() => onDelete(message)}
            className={`opacity-0 group-hover:opacity-100 transition-opacity ${
              isOwn ? 'text-white/70 hover:text-white' : 'text-slate-400 hover:text-red-500'
            }`}
            data-testid={`delete-message-${message.id}`}
          >
            <Trash2 className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  </div>
);

// Announcement Card Component
const AnnouncementCard = ({ announcement, onDelete, canDelete, isAdmin }) => {
  const config = priorityConfig[announcement.priority] || priorityConfig.info;
  const Icon = config.icon;
  
  return (
    <Card className={`border-l-4 ${config.color.replace('bg-', 'border-l-').replace('-100', '-500')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${config.color}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold text-slate-800">{announcement.title}</h4>
                <Badge className={config.color} variant="secondary">
                  {config.label}
                </Badge>
              </div>
              <p className="text-sm text-slate-600 mb-2">{announcement.message}</p>
              <div className="flex items-center gap-4 text-xs text-slate-400">
                <span>By {announcement.created_by_name}</span>
                <span>{formatET(announcement.created_at, 'MMM d, yyyy h:mm a')}</span>
                {announcement.target_company_name && (
                  <span className="flex items-center gap-1">
                    <Building2 className="h-3 w-3" />
                    {announcement.target_company_name}
                  </span>
                )}
              </div>
            </div>
          </div>
          {canDelete && (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onDelete(announcement)}
                className="h-8 w-8 text-red-500 hover:text-red-600"
                data-testid={`delete-announcement-${announcement.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const CommunicationsPage = () => {
  useDocumentTitle('Communications');
  const { user, isAdmin, isInternal, isPtcUser, canBulkDelete } = useAuth();
  const navigate = useNavigate();
  // v3.18-1377 GMAIL-002 Phase 1 — Mailbox is the primary comms surface.
  const [activeTab, setActiveTab] = useState('mailbox');
  
  // Messages state
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // New conversation state
  const [isNewConversationOpen, setIsNewConversationOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedNewUser, setSelectedNewUser] = useState('');
  
  // Announcements state
  const [announcements, setAnnouncements] = useState([]);
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(false);
  const [isAnnouncementDialogOpen, setIsAnnouncementDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [companies, setCompanies] = useState([]);
  
  // Delete confirmation state
  const [deleteMessageItem, setDeleteMessageItem] = useState(null);
  const [deleteAnnouncementItem, setDeleteAnnouncementItem] = useState(null);
  
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    message: '',
    priority: 'info',
    target_company_id: '',
  });

  // Applications state (for EasyQuickFund intake)
  const [applications, setApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [missedApplications, setMissedApplications] = useState([]);
  const [loadingMissedApps, setLoadingMissedApps] = useState(false);
  const [refreshingApps, setRefreshingApps] = useState(false);
  
  // Missed Applications bulk delete state
  const [selectedMissedIds, setSelectedMissedIds] = useState([]);
  const [selectAllMissed, setSelectAllMissed] = useState(false);
  const [deleteMissedDialogOpen, setDeleteMissedDialogOpen] = useState(false);
  const [deleteMissedMode, setDeleteMissedMode] = useState('selected'); // 'selected' or 'all'
  const [deleteMissedLoading, setDeleteMissedLoading] = useState(false);

  // Applications bulk delete state
  const [selectedAppIds, setSelectedAppIds] = useState([]);
  const [selectAllApps, setSelectAllApps] = useState(false);
  const [deleteAppDialogOpen, setDeleteAppDialogOpen] = useState(false);
  const [deleteAppMode, setDeleteAppMode] = useState('selected'); // 'selected', 'single', or 'all'
  const [deleteAppLoading, setDeleteAppLoading] = useState(false);
  const [singleDeleteAppId, setSingleDeleteAppId] = useState(null);

  // Convert to Deal state
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertAppId, setConvertAppId] = useState(null);
  const [converting, setConverting] = useState(false);

  // Fetch applications
  const fetchApplications = useCallback(async () => {
    if (!isPtcUser()) return;
    setLoadingApplications(true);
    try {
      const res = await api.get('/applications');
      setApplications(res.data || []);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setLoadingApplications(false);
    }
  }, [isPtcUser]);

  // Fetch missed applications
  const fetchMissedApplications = useCallback(async () => {
    if (!isPtcUser()) return;
    setLoadingMissedApps(true);
    try {
      const res = await api.get('/missed-applications');
      // Filter based on role - managers see all, reps see only their own
      let filtered = res.data || [];
      if (!['ptc_admin', 'ptc_manager', 'ptc_director'].includes(user?.role)) {
        filtered = filtered.filter(app => app.missed_rep_id === user?.id);
      }
      setMissedApplications(filtered);
    } catch (error) {
      console.error('Failed to fetch missed applications:', error);
    } finally {
      setLoadingMissedApps(false);
    }
  }, [isPtcUser, user?.role, user?.id]);

  // Handle refresh applications
  const handleRefreshApplications = async () => {
    setRefreshingApps(true);
    await Promise.all([fetchApplications(), fetchMissedApplications()]);
    setRefreshingApps(false);
    setSelectedMissedIds([]);
    setSelectAllMissed(false);
    setSelectedAppIds([]);
    setSelectAllApps(false);
    toast.success('Applications refreshed');
  };

  // Applications selection handlers
  const toggleAppSelection = (id) => {
    setSelectedAppIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleSelectAllApps = () => {
    if (selectAllApps) {
      setSelectedAppIds([]);
      setSelectAllApps(false);
    } else {
      setSelectedAppIds(applications.map(a => a.id));
      setSelectAllApps(true);
    }
  };

  const clearAppSelection = () => {
    setSelectedAppIds([]);
    setSelectAllApps(false);
  };

  const openDeleteAppDialog = (mode, singleId = null) => {
    setDeleteAppMode(mode);
    setSingleDeleteAppId(singleId);
    setDeleteAppDialogOpen(true);
  };

  const handleDeleteApplications = async () => {
    setDeleteAppLoading(true);
    try {
      if (deleteAppMode === 'all') {
        await api.delete('/applications/clear-all');
        toast.success('All applications cleared');
      } else if (deleteAppMode === 'single' && singleDeleteAppId) {
        await api.delete(`/applications/${singleDeleteAppId}`);
        toast.success('Application deleted');
      } else {
        await api.post('/applications/bulk-delete', { ids: selectedAppIds });
        toast.success(`Deleted ${selectedAppIds.length} applications`);
      }
      setDeleteAppDialogOpen(false);
      setSelectedAppIds([]);
      setSelectAllApps(false);
      setSingleDeleteAppId(null);
      fetchApplications();
    } catch (error) {
      toast.error('Failed to delete applications');
    } finally {
      setDeleteAppLoading(false);
    }
  };

  // Convert to Deal handlers
  const openConvertDialog = (appId) => {
    setConvertAppId(appId);
    setConvertDialogOpen(true);
  };

  const handleConvertToDeal = async () => {
    if (!convertAppId) return;
    
    setConverting(true);
    try {
      const response = await api.post(`/applications/${convertAppId}/convert-to-deal`);
      toast.success('Deal created successfully');
      setConvertDialogOpen(false);
      setConvertAppId(null);
      
      // Redirect to the new deal page
      const dealId = response.data.deal_id;
      navigate(`/clients/${dealId}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to convert application';
      toast.error(message);
    } finally {
      setConverting(false);
    }
  };

  // Missed applications selection handlers
  const toggleMissedSelection = (id) => {
    setSelectedMissedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleSelectAllMissed = () => {
    if (selectAllMissed) {
      setSelectedMissedIds([]);
      setSelectAllMissed(false);
    } else {
      setSelectedMissedIds(missedApplications.map(m => m.id));
      setSelectAllMissed(true);
    }
  };

  const clearMissedSelection = () => {
    setSelectedMissedIds([]);
    setSelectAllMissed(false);
  };

  const openDeleteMissedDialog = (mode) => {
    setDeleteMissedMode(mode);
    setDeleteMissedDialogOpen(true);
  };

  const handleDeleteMissedApplications = async () => {
    setDeleteMissedLoading(true);
    try {
      if (deleteMissedMode === 'all') {
        await api.delete('/missed-applications/clear-all');
        toast.success('All missed applications cleared');
      } else {
        await api.delete('/missed-applications/bulk-delete', { data: { ids: selectedMissedIds } });
        toast.success(`Deleted ${selectedMissedIds.length} missed applications`);
      }
      setDeleteMissedDialogOpen(false);
      setSelectedMissedIds([]);
      setSelectAllMissed(false);
      fetchMissedApplications();
    } catch (error) {
      toast.error('Failed to delete missed applications');
    } finally {
      setDeleteMissedLoading(false);
    }
  };

  // Navigate to client panel (NOT deal control panel)
  const handleViewDeal = (dealId) => {
    navigate(`/clients/${dealId}`);
  };

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const res = await api.get('/messages/conversations');
      setConversations(res.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    }
  }, []);

  // Fetch messages for selected conversation
  const fetchMessages = useCallback(async (userId) => {
    if (!userId) return;
    setLoadingMessages(true);
    try {
      const res = await api.get(`/messages/${userId}`);
      setMessages(res.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  // Fetch announcements
  const fetchAnnouncements = useCallback(async () => {
    setLoadingAnnouncements(true);
    try {
      const res = await api.get('/announcements?include_inactive=true');
      setAnnouncements(res.data);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    } finally {
      setLoadingAnnouncements(false);
    }
  }, []);

  // Fetch available users for new conversation
  const fetchAvailableUsers = useCallback(async () => {
    try {
      // Internal users can message anyone, external users can message their company + PTC reps
      const endpoint = isInternal() ? '/users' : '/users/my-team';
      const res = await api.get(endpoint);
      // Filter out current user
      setAvailableUsers(res.data.filter(u => u.id !== user?.id));
      
      // If internal, also get PTC reps for external users
      if (!isInternal()) {
        const ptcRes = await api.get('/users/ptc-reps');
        const combined = [...res.data, ...ptcRes.data].filter(u => u.id !== user?.id);
        // Remove duplicates
        const unique = combined.filter((v, i, a) => a.findIndex(t => t.id === v.id) === i);
        setAvailableUsers(unique);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [isInternal, user?.id]);

  // Fetch companies for announcement targeting
  const fetchCompanies = useCallback(async () => {
    try {
      const res = await api.get('/companies');
      setCompanies(res.data);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
    fetchAnnouncements();
    if (isAdmin()) {
      fetchCompanies();
    }
    // Fetch applications data for PTC users only (not Prestigious Travel Club)
    if (isPtcUser()) {
      fetchApplications();
      fetchMissedApplications();
    }
    
    // Subscribe to WebSocket for real-time messages
    const unsubMessage = wsService.subscribe('message', (data) => {
      console.log('Real-time message received:', data);
      // Refresh conversations and messages
      fetchConversations();
      if (selectedConversation && data.sender_id === selectedConversation.user_id) {
        fetchMessages(selectedConversation.user_id);
      }
    });
    
    const unsubAnnouncement = wsService.subscribe('announcement', (data) => {
      console.log('Real-time announcement received:', data);
      // Refresh announcements
      fetchAnnouncements();
    });

    // Subscribe to new application events
    const unsubNewApp = wsService.subscribe('new_application', () => {
      fetchApplications();
    });

    const unsubMissedApp = wsService.subscribe('application_missed', () => {
      fetchMissedApplications();
    });
    
    // Auto-refresh conversations every 30 seconds (fallback)
    const refreshInterval = setInterval(() => {
      fetchConversations();
      if (selectedConversation) {
        fetchMessages(selectedConversation.user_id);
      }
      // Also refresh applications if on that tab (PTC users only)
      if (activeTab === 'applications' && isPtcUser()) {
        fetchApplications();
      }
    }, 30000);
    
    return () => {
      clearInterval(refreshInterval);
      unsubMessage();
      unsubAnnouncement();
      unsubNewApp();
      unsubMissedApp();
    };
  }, [fetchConversations, fetchAnnouncements, fetchCompanies, isAdmin, selectedConversation, fetchMessages, isPtcUser, fetchApplications, fetchMissedApplications, activeTab]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.user_id);
    }
  }, [selectedConversation, fetchMessages]);

  useEffect(() => {
    if (isNewConversationOpen) {
      fetchAvailableUsers();
    }
  }, [isNewConversationOpen, fetchAvailableUsers]);

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSendingMessage(true);
    try {
      await api.post('/messages', {
        recipient_id: selectedConversation.user_id,
        content: newMessage,
        message_type: 'general',
      });
      setNewMessage('');
      fetchMessages(selectedConversation.user_id);
      fetchConversations();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send message'));
    } finally {
      setSendingMessage(false);
    }
  };

  // Start new conversation
  const handleStartConversation = async () => {
    if (!selectedNewUser || !newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      await api.post('/messages', {
        recipient_id: selectedNewUser,
        content: newMessage,
        message_type: 'general',
      });
      
      setIsNewConversationOpen(false);
      setNewMessage('');
      setSelectedNewUser('');
      fetchConversations();
      
      // Select the new conversation
      const newConv = availableUsers.find(u => u.id === selectedNewUser);
      if (newConv) {
        setSelectedConversation({
          user_id: newConv.id,
          user_name: newConv.full_name,
          user_company: newConv.company_name,
        });
      }
      
      toast.success('Message sent');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send message'));
    } finally {
      setSendingMessage(false);
    }
  };

  // Create/Update announcement
  const handleSaveAnnouncement = async () => {
    if (!announcementForm.title.trim() || !announcementForm.message.trim()) {
      toast.error('Title and message are required');
      return;
    }
    
    try {
      const payload = {
        title: announcementForm.title,
        message: announcementForm.message,
        priority: announcementForm.priority,
        target_company_id: announcementForm.target_company_id || null,
      };
      
      if (editingAnnouncement) {
        await api.put(`/announcements/${editingAnnouncement.id}`, payload);
        toast.success('Announcement updated');
      } else {
        await api.post('/announcements', payload);
        toast.success('Announcement created');
      }
      
      setIsAnnouncementDialogOpen(false);
      setEditingAnnouncement(null);
      setAnnouncementForm({ title: '', message: '', priority: 'info', target_company_id: '' });
      fetchAnnouncements();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save announcement'));
    }
  };

  // Check if user can delete a message
  const canDeleteMessage = (message) => {
    // PTC Admin can delete any message
    if (isAdmin()) return true;
    // User can delete their own messages
    return message.sender_id === user?.id;
  };

  // Check if user can delete an announcement
  const canDeleteAnnouncement = (announcement) => {
    // PTC Admin can delete any announcement
    if (isAdmin()) return true;
    // User can delete their own announcements
    if (announcement.created_by === user?.id) return true;
    // Company director can delete announcements for their company
    if (user?.role === 'company_director' && announcement.target_company_id === user?.company_id) return true;
    return false;
  };

  // Delete message with confirmation
  const handleDeleteMessage = async () => {
    if (!deleteMessageItem) return;
    
    try {
      await api.delete(`/messages/${deleteMessageItem.id}`);
      toast.success('Message deleted');
      setDeleteMessageItem(null);
      // Refresh messages
      if (selectedConversation) {
        fetchMessages(selectedConversation.user_id);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete message'));
    }
  };

  // Delete announcement with confirmation
  const handleDeleteAnnouncement = async () => {
    if (!deleteAnnouncementItem) return;
    
    try {
      await api.delete(`/announcements/${deleteAnnouncementItem.id}`);
      toast.success('Announcement deleted');
      setDeleteAnnouncementItem(null);
      fetchAnnouncements();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete announcement'));
    }
  };

  // Delete ALL announcements (Admin only)
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  
  const handleDeleteAllAnnouncements = async () => {
    setDeletingAll(true);
    try {
      const response = await api.delete('/announcements');
      toast.success(response.data.message || 'All announcements deleted');
      setShowDeleteAllDialog(false);
      fetchAnnouncements();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete announcements'));
    } finally {
      setDeletingAll(false);
    }
  };

  // Edit announcement
  const handleEditAnnouncement = (announcement) => {
    setEditingAnnouncement(announcement);
    setAnnouncementForm({
      title: announcement.title,
      message: announcement.message,
      priority: announcement.priority,
      target_company_id: announcement.target_company_id || '',
    });
    setIsAnnouncementDialogOpen(true);
  };

  return (
    <div className="space-y-4" data-testid="communications-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-primary">Communications Center</h1>
          <p className="text-sm text-slate-500 mt-1">
            Email · Team messaging · Announcements · Bulletin · Drive
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* v3.19-1422 COMMS-MODERN — Final tab order per spec:
              1. Company Email  (was: Mailbox)
              2. Internal Messaging (was: Messages, now Google Chat launcher)
              3. Announcements  (unchanged)
              4. Bulletin Board (new)
              5. Company Drive  (new, stubbed)
            Applications + Missed tabs removed. */}
        {/* v3.19-1430 · Tab order:
              1. Company Email
              2. Internal Messaging (NATIVE, real-time, primary)
              3. Google Chat (External)  — optional connector
              4. Announcements
              5. Bulletin Board
              6. Company Drive
            Native Messaging is the default; Google Chat is external. */}
        <TabsList className="grid w-full grid-cols-7 max-w-5xl">
          <TabsTrigger value="mailbox" className="flex items-center gap-2" data-testid="company-email-tab-trigger">
            <Mail className="h-4 w-4" />
            Company Email
          </TabsTrigger>
          <TabsTrigger value="messages" className="flex items-center gap-2" data-testid="internal-messaging-tab-trigger">
            <MessageSquare className="h-4 w-4" />
            Internal Messaging
          </TabsTrigger>
          {/* v3.21-1736 · Communications Inbox now includes real SMS. The
              tab reads /api/sms-dashboard/conversations (Deal-Ownership
              scoped server-side) and deep-links to /sms-dashboard for
              full thread handling.  sms_messages remains the content
              SSoT — no message-body duplication. */}
          <TabsTrigger value="sms" className="flex items-center gap-2" data-testid="communications-sms-tab-trigger">
            <MessageSquare className="h-4 w-4 text-blue-600" />
            SMS
          </TabsTrigger>
          <TabsTrigger value="google-chat" className="flex items-center gap-2" data-testid="google-chat-external-tab-trigger">
            <MessageSquare className="h-4 w-4" />
            Google Chat <span className="text-[10px] text-slate-500 font-normal">(External)</span>
          </TabsTrigger>
          <TabsTrigger value="announcements" className="flex items-center gap-2" data-testid="announcements-tab-trigger">
            <Bell className="h-4 w-4" />
            Announcements
          </TabsTrigger>
          {/* v3.21-1613 · Bulletin Board is INTERNAL PTC ONLY.
              External partner users must never see this tab, and the
              backend enforces the boundary with HTTP 403. */}
          {isInternal() && (
            <TabsTrigger value="bulletin" className="flex items-center gap-2" data-testid="bulletin-tab-trigger">
              <Bell className="h-4 w-4" />
              Bulletin Board
            </TabsTrigger>
          )}
          <TabsTrigger value="drive" className="flex items-center gap-2" data-testid="drive-tab-trigger">
            <FileText className="h-4 w-4" />
            Company Drive
          </TabsTrigger>
        </TabsList>

        {/* v3.18-1377 GMAIL-002 Phase 1 — Mailbox tab renders the
            Inbox/Sent/Drafts/Compose UI shell. Content will populate
            in Phase 2 once the Gmail sync engine lands. */}
        <TabsContent value="mailbox" className="mt-6" data-testid="mailbox-tab-content">
          <MailboxTab />
        </TabsContent>

        {/* v3.19-1430 · Internal Messaging tab renders the NATIVE
            Hub messaging client (WebSocket real-time, no Google
            dependency). */}
        <TabsContent value="messages" className="mt-6" data-testid="internal-messaging-content">
          <NativeMessagingTab />
        </TabsContent>

        {/* v3.21-1736 · SMS tab — Communications Inbox integration. */}
        <TabsContent value="sms" className="mt-6" data-testid="communications-sms-content">
          <CommunicationsSmsTab />
        </TabsContent>

        {/* v3.19-1430 · Google Chat is now an OPTIONAL external
            connector.  Kept for teams that live in Google Workspace
            Chat; not the default. */}
        <TabsContent value="google-chat" className="mt-6" data-testid="google-chat-external-content">
          <InternalMessagingTab />
        </TabsContent>

        {/* Announcements Tab */}
        <TabsContent value="announcements" className="mt-6">
          <div className="space-y-4">
            {/* Create button for admins */}
            {isAdmin() && (
              <div className="flex justify-end">
                <Dialog open={isAnnouncementDialogOpen} onOpenChange={(open) => {
                  setIsAnnouncementDialogOpen(open);
                  if (!open) {
                    setEditingAnnouncement(null);
                    setAnnouncementForm({ title: '', message: '', priority: 'info', target_company_id: '' });
                  }
                }}>
                  <div className="flex gap-2">
                    {/* Delete All Button - Admin Only */}
                    {isAdmin() && announcements.length > 0 && (
                      <Button 
                        variant="outline" 
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => setShowDeleteAllDialog(true)}
                        data-testid="delete-all-announcements-btn"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete All
                      </Button>
                    )}
                    <DialogTrigger asChild>
                      <Button className="bg-accent hover:bg-gold-dark" data-testid="create-announcement-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Create Announcement
                      </Button>
                    </DialogTrigger>
                  </div>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>
                        {editingAnnouncement ? 'Edit Announcement' : 'Create Announcement'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Title *</Label>
                        <Input
                          value={announcementForm.title}
                          onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
                          placeholder="Announcement title"
                          data-testid="announcement-title"
                        />
                      </div>
                      <div>
                        <Label>Message *</Label>
                        <Textarea
                          value={announcementForm.message}
                          onChange={(e) => setAnnouncementForm({ ...announcementForm, message: e.target.value })}
                          placeholder="Announcement message"
                          rows={4}
                          data-testid="announcement-message"
                        />
                      </div>
                      <div>
                        <Label>Priority</Label>
                        <Select 
                          value={announcementForm.priority} 
                          onValueChange={(v) => setAnnouncementForm({ ...announcementForm, priority: v })}
                        >
                          <SelectTrigger data-testid="announcement-priority">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="info">Info (Blue)</SelectItem>
                            <SelectItem value="warning">Warning (Orange)</SelectItem>
                            <SelectItem value="critical">Critical (Red)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Target Company (optional)</Label>
                        <Select 
                          value={announcementForm.target_company_id || "all"} 
                          onValueChange={(v) => setAnnouncementForm({ ...announcementForm, target_company_id: v === "all" ? "" : v })}
                        >
                          <SelectTrigger data-testid="announcement-target">
                            <SelectValue placeholder="All Companies" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Companies</SelectItem>
                            {companies.map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button 
                        className="w-full bg-accent hover:bg-gold-dark"
                        onClick={handleSaveAnnouncement}
                        data-testid="save-announcement-btn"
                      >
                        {editingAnnouncement ? 'Update' : 'Create'} Announcement
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            )}

            {/* Announcements List */}
            {loadingAnnouncements ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
              </div>
            ) : announcements.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Bell className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No announcements</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {announcements.map((ann) => (
                  <AnnouncementCard
                    key={ann.id}
                    announcement={ann}
                    canDelete={canDeleteAnnouncement(ann)}
                    onDelete={(a) => setDeleteAnnouncementItem(a)}
                    isAdmin={isAdmin()}
                  />
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* v3.19-1422 COMMS-MODERN — Bulletin Board (new)
            v3.21-1613 · Internal PTC users only. */}
        {isInternal() && (
        <TabsContent value="bulletin" className="mt-6" data-testid="bulletin-content">
          <BulletinBoardTab />
        </TabsContent>
        )}

        {/* v3.19-1422 COMMS-MODERN — Company Drive (stub) */}
        <TabsContent value="drive" className="mt-6" data-testid="drive-content">
          <CompanyDriveTab />
        </TabsContent>
      </Tabs>

      {/* Delete Message Confirmation Dialog */}
      <AlertDialog open={!!deleteMessageItem} onOpenChange={() => setDeleteMessageItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Message?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this message? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteMessage}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-message"
            >
              Delete Message
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Announcement Confirmation Dialog */}
      <AlertDialog open={!!deleteAnnouncementItem} onOpenChange={() => setDeleteAnnouncementItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Announcement?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this announcement? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAnnouncement}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-announcement"
            >
              Delete Announcement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete ALL Announcements Confirmation Dialog */}
      <AlertDialog open={showDeleteAllDialog} onOpenChange={setShowDeleteAllDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete ALL Announcements?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {announcements.length} announcement(s). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingAll}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAllAnnouncements}
              className="bg-red-500 hover:bg-red-600"
              disabled={deletingAll}
              data-testid="confirm-delete-all-announcements"
            >
              {deletingAll ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete All'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Missed Applications Confirmation Dialog */}
      <AlertDialog open={deleteMissedDialogOpen} onOpenChange={setDeleteMissedDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Deletion
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteMissedMode === 'all' 
                ? `You are about to permanently delete ${missedApplications.length} missed application records.`
                : `You are about to permanently delete ${selectedMissedIds.length} selected records.`
              }
              <br /><span className="font-medium text-red-600">This cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMissedLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteMissedApplications}
              className="bg-red-500 hover:bg-red-600"
              disabled={deleteMissedLoading}
              data-testid="confirm-delete-missed-btn"
            >
              {deleteMissedLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Applications Confirmation Dialog */}
      <AlertDialog open={deleteAppDialogOpen} onOpenChange={setDeleteAppDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Deletion
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteAppMode === 'all' 
                ? `You are about to permanently delete ${applications.length} applications.`
                : deleteAppMode === 'single'
                ? 'Are you sure you want to delete this application?'
                : `You are about to permanently delete ${selectedAppIds.length} selected applications.`
              }
              <br /><span className="font-medium text-red-600">This cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteAppLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteApplications}
              className="bg-red-500 hover:bg-red-600"
              disabled={deleteAppLoading}
              data-testid="confirm-delete-app-btn"
            >
              {deleteAppLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* v3.14-174: Convert to Deal dialog REMOVED — leads are active immediately */}
    </div>
  );
};

export default CommunicationsPage;
